/**
 * Date and time labels for display.
 *
 * Presentation lives here rather than in the components so that one weekday
 * spelling is used everywhere. The old codebase declared four separate day-name
 * arrays (`DOW`, a second incompatible `DOW`, `DL`, and a set of initials) that
 * shared an index contract but not their labels.
 *
 * Audit references prevented here:
 *   constants.js #5   (DOW forked into an incompatible second version)
 *   calories.jsx #8   (three separate day-name arrays in one file)
 *   nutrition.js #14  (a domain module returning preformatted English)
 */

import { APP_TZ, APP_TZ_LABEL } from './app-timezone.js';
import { anchorInstantForDateKey } from './date-key.js';
import { assertInstant } from './zoned-parts.js';

const LABEL_FORMATTER_CACHE = new Map();

function labelFormatter(cacheKey, options) {
  const cached = LABEL_FORMATTER_CACHE.get(cacheKey);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat('en-US', options);
  LABEL_FORMATTER_CACHE.set(cacheKey, formatter);
  return formatter;
}

/**
 * Full weekday name for a date key, for example "Monday".
 *
 * @param {string} dateKey
 * @returns {string}
 */
export function weekdayLongForDateKey(dateKey) {
  const formatter = labelFormatter('weekdayLong', { timeZone: 'UTC', weekday: 'long' });
  return formatter.format(anchorInstantForDateKey(dateKey));
}

/**
 * Abbreviated weekday name for a date key, for example "Mon".
 *
 * Derived from the date rather than indexed out of an array, so a caller drawing a row of
 * days never has to declare whether its week starts on Sunday or Monday. That index
 * convention is the whole reason this file exists.
 *
 * @param {string} dateKey
 * @returns {string}
 */
export function weekdayShortForDateKey(dateKey) {
  const formatter = labelFormatter('weekdayShort', { timeZone: 'UTC', weekday: 'short' });
  return formatter.format(anchorInstantForDateKey(dateKey));
}

/**
 * Short month and day for a date key, for example "Aug 3".
 *
 * @param {string} dateKey
 * @returns {string}
 */
export function monthDayForDateKey(dateKey) {
  const formatter = labelFormatter('monthDay', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  });
  return formatter.format(anchorInstantForDateKey(dateKey));
}

/**
 * A stored `HH:mm` time of day, as it should be shown. For example "5:00 PM".
 *
 * Times are stored 24-hour and were being printed both ways: the planned-session card
 * formatted them, while the calendar day rows printed the raw value, so the same session
 * read as "5:00 PM – 6:00 PM" in one place and "17:00–18:00" in another. This is the one
 * spelling, for the same reason the weekday names in this file are.
 *
 * The anchor date is arbitrary and formatted in UTC: only the clock face matters, and
 * pinning the zone keeps the output identical wherever the suite or the user runs.
 *
 * @param {string} time  `HH:mm`
 * @returns {string|null} Null when there is no time to show.
 */
export function timeOfDayLabel(time) {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) return null;
  const [hour, minute] = time.split(':').map(Number);
  const formatter = labelFormatter('timeOfDay', { timeZone: 'UTC', hour: 'numeric', minute: '2-digit' });
  return formatter.format(new Date(Date.UTC(2026, 0, 1, hour, minute)));
}

/**
 * A start and end time as one range, for example "5:00 PM to 6:00 PM".
 *
 * @param {string|null} start
 * @param {string|null} end
 * @returns {string} Empty when neither end of the range is known.
 */
export function timeRangeLabel(start, end) {
  return [timeOfDayLabel(start), timeOfDayLabel(end)].filter(Boolean).join(' to ');
}

/**
 * Wall clock time in the application timezone, to the second, without a zone
 * suffix. For example "12:22:40 AM".
 *
 * @param {Date} instant
 * @returns {string}
 */
export function clockTimeForInstant(instant) {
  assertInstant(instant);
  const formatter = labelFormatter('clockWithSeconds', {
    timeZone: APP_TZ,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
  return formatter.format(instant);
}

/**
 * Wall clock time with the application timezone label, for example
 * "12:22:40 AM ET".
 *
 * @param {Date} instant
 * @returns {string}
 */
export function clockLabelForInstant(instant) {
  return `${clockTimeForInstant(instant)} ${APP_TZ_LABEL}`;
}

/**
 * A calendar date in full, for example "August 10, 2026" - used where a moment is
 * old enough that the day itself is the useful fact, not how long ago it was.
 *
 * @param {Date} instant
 * @returns {string}
 */
export function longDateLabel(instant) {
  assertInstant(instant);
  const formatter = labelFormatter('longDate', { timeZone: APP_TZ, month: 'long', day: 'numeric', year: 'numeric' });
  return formatter.format(instant);
}

/**
 * How long ago a moment was, in the coarsest unit that still reads as "recent" -
 * for example "5 minutes ago", "3 hours ago", "2 days ago" - falling back to the
 * calendar date once it is far enough in the past that a count of days stops being
 * the useful fact.
 *
 * @param {Date} instant
 * @param {Date} [now]
 * @returns {string}
 */
export function relativeTimeLabel(instant, now = new Date()) {
  assertInstant(instant);
  assertInstant(now);
  const seconds = Math.max(0, Math.round((now.getTime() - instant.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return longDateLabel(instant);
}
